import { useMemo, useState, useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../hooks';
import { IOtherEmployee, IRequestPhone, IOtherPerson } from '../../../types';
import { Wrapper, ToggleWrapper } from './third-party-accounts.styled';
import { ThirdPartyAccountsSelect, AddModal } from './components';
import { Toggle } from '../../index';
import { ActionItem } from 'styles/animations.styled';
import { ApplicantCard } from '../index';
import { normalizePhone, getFullName } from 'utils';

interface Props {
  canCollapsed?: boolean;
  withoutCallback?: boolean;
  id?: string;
  inCallCard?: boolean;
  card?: any;
}

export const ThirdPartyAccounts = observer(
  ({
    canCollapsed = true,
    withoutCallback = false,
    id,
    inCallCard,
    card,
  }: Props) => {
    const { createRequest } = useStore();

    const ref = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState<boolean>(false);

    const isCollapsed: boolean = useMemo(() => canCollapsed && !open, [open]);

    const transformPerson = (person: IOtherPerson | IOtherEmployee) => ({
      email: person.email,
      first_name: person.first_name,
      last_name: person.last_name,
      patronymic_name: person.patronymic_name,
      str_name: getFullName(person),
      id: person._id,
      phones: person.phone_numbers.map((phone: IRequestPhone) =>
        normalizePhone(phone)
      ),
    });

    const providerName =
      createRequest.applicant.allOtherProviders.find(
        (provider) => provider._id === createRequest.applicant.organizationId
      )?.name || '';

    const transformedPersons = createRequest.applicant.allOtherPersons.map(
      (person) => transformPerson(person)
    );

    const transformedEmployees = createRequest.applicant.allOtherEmployees.map(
      (employee) => ({
        ...transformPerson(employee),
        provider_id: employee.other_provider_id,
        position: employee.position_name,
      })
    );

    const otherPersonAccounts = (): JSX.Element[] => {
      const otherPersonCardArr: JSX.Element[] = [];
      transformedPersons.forEach((person): void => {
        otherPersonCardArr.push(
          <ApplicantCard
            key={person.id}
            applicant={person}
            selected={createRequest.applicant.physicalId === person.id}
            collapsed={!open}
            physicalId={person.id}
            withoutCallback={withoutCallback}
            id={id}
            inCallCard={inCallCard}
            card={card}
          />
        );
      });

      return otherPersonCardArr;
    };

    const otherEmployeeAccounts = (): JSX.Element[] => {
      const otherEmployeeCardArr: JSX.Element[] = [];
      transformedEmployees.forEach((employee): void => {
        otherEmployeeCardArr.push(
          <ApplicantCard
            key={employee.id}
            applicant={employee}
            selected={createRequest.applicant.legalId === employee.id}
            collapsed={isCollapsed}
            canCollapsed={canCollapsed}
            service_name={providerName}
            organizationId={employee.provider_id}
            withoutCallback={withoutCallback}
            inCallCard={inCallCard}
            card={card}
          />
        );
      });

      return otherEmployeeCardArr;
    };

    const currentThirdPartyAccounts: JSX.Element[] = useMemo(() => {
      return createRequest.applicant.thirdPartyType === 'other_employee'
        ? otherEmployeeAccounts()
        : otherPersonAccounts();
    }, [
      createRequest.applicant.thirdPartyType,
      createRequest.applicant.legalId,
      createRequest.applicant.physicalId,
      open,
      createRequest.applicant.loading.addContacts.pending,
    ]);

    const addNewOtherWorker = (): void => {
      createRequest.setAddNewThirdPartyModalProperties(
        'addOrganization',
        false
      );
      createRequest.setAddNewThirdPartyModalProperties('show', true);
    };

    const handleOpen = (): void => {
      setOpen((prevOpen) => !prevOpen);
    };

    useEffect(() => {
      if (
        !createRequest.initialPhoneNumber ||
        createRequest.applicant.thirdPartyType !== 'other_person' ||
        createRequest.applicant.loading.otherPersonAccounts.pending
      ) {
        return;
      }

      const matchingPerson = createRequest.applicant.allOtherPersons.find(
        (person) =>
          person.phone_numbers.some(
            (phone) => phone.number === createRequest.initialPhoneNumber
          )
      );

      matchingPerson &&
        createRequest.applicant.setSelectedId('physicalId', matchingPerson._id);
    }, [
      createRequest.initialPhoneNumber,
      createRequest.applicant.allOtherPersons,
      createRequest.applicant.setSelectedId,
      createRequest.applicant.loading.otherPersonAccounts,
      createRequest.applicant.thirdPartyType,
      createRequest.applicant.allOtherEmployees,
    ]);

    useEffect(() => {
      if (
        !createRequest.initialPhoneNumber ||
        createRequest.applicant.thirdPartyType !== 'other_employee' ||
        createRequest.applicant.loading.otherProviderAccounts.pending ||
        !createRequest.applicant.allOtherEmployees?.[0]?._id ||
        (createRequest.applicant.organizationId &&
          createRequest.applicant.legalId)
      ) {
        return;
      }

      createRequest.applicant.setSelectedId(
        'organizationId',
        createRequest.applicant.allOtherEmployees[0].other_provider_id
      );
      createRequest.applicant.setSelectedId(
        'legalId',
        createRequest.applicant.allOtherEmployees[0]._id
      );
    }, [
      createRequest.initialPhoneNumber,
      createRequest.applicant.thirdPartyType,
      createRequest.applicant.allOtherEmployees,
      createRequest.applicant.loading.otherProviderAccounts.pending, // только нужное поле
      createRequest.applicant.setSelectedId,
    ]);

    return (
      <Wrapper ref={ref} collapsed={isCollapsed}>
        {canCollapsed && (
          <ToggleWrapper>
            <Toggle
              open={open}
              onClick={handleOpen}
              disabled={!currentThirdPartyAccounts.length}
            />
          </ToggleWrapper>
        )}

        <ThirdPartyAccountsSelect collapsed={isCollapsed} card={card} />

        {currentThirdPartyAccounts.map((item: JSX.Element) => item)}

        {
          // userThirdPartyRights.create &&
          (createRequest.applicant.thirdPartyType === 'other_person' ||
            (createRequest.applicant.thirdPartyType === 'other_employee' &&
              createRequest.applicant.organizationId !== '')) && (
            <ActionItem
              isCollapsed={!isCollapsed}
              isSelected={
                !currentThirdPartyAccounts.length ||
                (createRequest.applicant.thirdPartyType === 'other_person' &&
                  !createRequest.applicant.physicalId)
              }
              onClick={addNewOtherWorker}
            >
              {createRequest.applicant.thirdPartyType === 'other_employee'
                ? 'Добавить представителя организации'
                : 'Добавить'}
            </ActionItem>
          )
        }

        {createRequest.addNewThirdPartyModal.show && <AddModal />}
      </Wrapper>
    );
  }
);
